import { FramesManager } from '../../../view/frames-manager'
import { UI2IconName } from '../../icons/ui2-icon-names'
import { EditableFieldProps } from '../core-field/editable-field-json-props'

export type EntitySearchMode = 'auto' | 'submit'
export type EntitySearchCreatableMode = 'primary' | 'secondary'

/** Payload for `onEntityChange`. `entity` is undefined when the field is cleared. */
export interface EntitySearchSelectionPayload {
  entityId: string | undefined
  entity: any | undefined
}

/** Payload for `onSubmitSearch` (submit mode). */
export interface EntitySearchSubmitPayload {
  hasResults: boolean
  numberOfAttempts: number
}

/**
 * Search-and-select field for entities. Emits an edge reference on the
 * bound path via `onChange`; the picked entity is delivered separately
 * via `onEntityChange` so schema authors can react to selection without
 * intercepting the renderer's edge write.
 *
 * ### Search modes
 * - `'auto'` — modal opens on tap and searches as the user types.
 * - `'submit'` — inline input; user types a full query, hits submit,
 *   results render in a tethered dropdown below. Common for
 *   driver-facing flows.
 */
export const UI2_ENTITY_SEARCH_FIELD_DEFAULT_PROPS: Partial<UI2EntitySearchFieldProps> = {
  minQueryLength: 3,
  searchMode: 'auto',
  isClearable: true,
  creatableMode: 'secondary',
  entitySearchResultCardSchemaPath: 'uiSchema.shared.entitySearchResultCardSchema',
  searchHelpSchemaPath: 'uiSchema.shared.searchHelpSchema',
  entityPreviewCardSchemaPath: 'uiSchema.shared.entityPreviewCardSchema',
  searchEmptyResultsSchemaPath: 'uiSchema.shared.searchEmptyResultsSchema',
  entityCreateSchemaPath: 'uiSchema.shared.entityCreateSchema',
  entityEditSchemaPath: 'uiSchema.shared.entityEditSchema',
  entityReadOnlySchemaPath: 'uiSchema.shared.entityReadOnlySchema',
}

export interface UI2EntitySearchFieldProps extends EditableFieldProps {
  // ─── Selection callbacks ───────────────────────────────────────────────
  /**
   * Fires on selection with the selected entity id and hydrated entity.
   * Separate from `onChange` (renderer-owned) so authors can react to the
   * picked entity without intercepting the edge write.
   */
  onEntityChange?: (payload: EntitySearchSelectionPayload) => void
  /** Dot path to the value written for each selected option. */
  optionValuePath?: string
  /**
   * Maps fields on the selected entity to values written elsewhere on the
   * bound path (denormalized edges). Formulas have access to `searchFilter`,
   * `entity`, plus the current frames' state.
   */
  valueMappings?: any[]

  // ─── Search behavior ───────────────────────────────────────────────────
  /** Base search filter/query for the entity index. */
  query?: any
  /** Minimum characters before the search runs. Defaults to 3. */
  minQueryLength?: number
  /** Auto (default) or submit mode. */
  searchMode?: EntitySearchMode
  /** Fires when the user submits a query in submit mode. */
  onSubmitSearch?: (payload: EntitySearchSubmitPayload) => void
  /**
   * Fires when the user applies filter changes in the search modal's
   * filter sheet. Callers can persist the applied/available lists so they
   * survive across sessions.
   */
  onFiltersChange?: (filters: any[], availableFilters?: any[]) => void
  /**
   * Transform the raw user-typed filter before it hits the search index
   * (e.g. strip non-digits from a phone-number search).
   */
  transformSearchFilterFn?: (input: string) => string
  /** Transform the results list before rendering. */
  transformResultsFn?: (contextArgs: any) => any[]
  /** Prefetch results before the user types. Only takes effect in auto mode. */
  preloadResults?: boolean
  autoFocus?: boolean
  isClearable?: boolean

  /**
   * Enables multi-select mode. Supported in `auto` mode only; silently
   * ignored when `searchMode` is `'submit'`. When active, `value` is an
   * array of `{entityId, displayName}` objects and `openEntityOnSelect` is
   * ignored.
   */
  isMultiSelect?: boolean

  // ─── Auto-mode-only options ────────────────────────────────────────────
  /**
   * When true, tapping a search result highlights it instead of committing
   * immediately; the user commits via a pinned confirm button. Auto mode
   * only.
   */
  confirmOnSelect?: boolean
  /**
   * When set, automatically opens an entity UI after the user selects an
   * entity from the auto-mode modal.
   * - `'edit'`     — opens the edit UI; falls back to read-only if
   *                  `entityEditSchemaPath` doesn't resolve, then skips if
   *                  neither resolves.
   * - `'readonly'` — always opens the read-only UI (skips if the path
   *                  doesn't resolve).
   * - `'auto'`     — opens edit when the selected entity belongs to the
   *                  viewer's firm; opens read-only for cross-firm entities.
   *                  Same edit→read-only fallback as `'edit'`.
   * Auto mode only.
   */
  openEntityOnSelect?: 'edit' | 'readonly' | 'auto'
  /**
   * Expression producing the confirm button label. Evaluated with the
   * parent workflow context plus `selectedItem`/`item` and `searchFilter`.
   * Auto mode only.
   */
  confirmButtonLabel?: string

  // ─── Submit-mode-only options ──────────────────────────────────────────
  /**
   * When set, submit mode surfaces an option to commit the raw typed
   * value to this path instead of selecting an entity. Submit mode only.
   */
  rawQueryPath?: string
  /**
   * Label template for the raw-submit option. Use `{searchFilter}` as a
   * placeholder for the current query. Submit mode only.
   */
  rawSubmitLabel?: string

  /** Glyph to associate with the search field's entity type. No default — omitted when unset. */
  iconName?: UI2IconName

  // ─── Search modal chrome ───────────────────────────────────────────────
  /** Title for the search modal header. Defaults to "Find <EntityType>". */
  searchModalTitle?: string
  /** Subtitle under the title in the search modal header. */
  searchModalSubtitle?: string

  // ─── Entity creation ───────────────────────────────────────────────────
  /**
   * When true and `entityCreateSchemaPath` resolves, the search surface
   * offers a "Create <EntityType>" affordance. Tapping it opens the
   * create sheet; on save the new entity is committed as the value.
   */
  isCreatable?: boolean
  /**
   * 'secondary' (default) — Create renders as a small button at the end
   *   of the results list ("create only if nothing found").
   * 'primary' — Create renders as a prominent action above/below the
   *   results list. Use when creation is first-class.
   */
  creatableMode?: EntitySearchCreatableMode

  // ─── Schema paths for related surfaces ─────────────────────────────────
  /**
   * Extra renderer scope for every sub-schema this field opens — the create,
   * edit and read-only sheets, plus the search modal's help / empty-results
   * subschemas. Keys land at the top level of the sub-schema's scope, so a
   * sub-schema reads `requiresLicensePlate` directly.
   *
   * Same channel and same name as `EntityFormSheet` / `EntityFormModal`'s
   * `extraState`, and the same shape as the legacy `ui:entitySelectField`'s
   * `entityCreateState` / `entityEditState`.
   *
   * Values follow the normal literal-or-expression convention: a literal is
   * passed through, and `{type: 'expression', value: '…'}` is evaluated in
   * *this* field's frames (the parent workflow) before being handed down.
   * That is the point — it lets facility rules the sub-schema's own entity
   * knows nothing about drive how that entity's form renders. Unlike
   * `valueMappings`, nothing here is written to the entity.
   *
   * Spread last into the sub-schema's scope, so a key named `entity`,
   * `settings` or `errors` shadows the real one. Name keys for what they are.
   */
  extraState?: { [key: string]: any }

  /** UI subschema for the create sheet. Default: 'uiSchema.shared.entityCreateSchema'. */
  entityCreateSchemaPath?: string
  /** UI subschema for the edit sheet opened from the preview card. */
  entityEditSchemaPath?: string
  /** UI subschema for the read-only sheet opened from the preview card. */
  entityReadOnlySchemaPath?: string
  /** UI subschema for a single search-result card. */
  entitySearchResultCardSchemaPath?: string
  /** UI subschema for the selected-entity preview card. */
  entityPreviewCardSchemaPath?: any
  /** UI subschema for the empty-results affordance in the search surface. */
  searchEmptyResultsSchemaPath?: string
  /** UI subschema for the pre-typing help surface in the search modal. */
  searchHelpSchemaPath?: string

  // ─── Renderer plumbing ─────────────────────────────────────────────────
  entity?: any
  /** Entity used as the execution context for expression evaluation. */
  executionEntity?: any
  translationTable?: any
  frames?: FramesManager
}
